import React, { useEffect } from "react";
import { X, Plus, Minus, Trash2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";

const CartDrawer = () => {
  const { t } = useLang();
  const { items, isOpen, closeCart, removeItem, updateQty, subtotal, count, clear } = useCart();

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="cart-backdrop"
        onClick={closeCart}
        className={`fixed inset-0 z-50 bg-black/70 backdrop-blur-sm transition-opacity duration-500 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[440px] bg-[#0a0a0a] border-l border-white/5 transform transition-transform duration-500 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-white/5">
            <div>
              <p className="overline">{t("cart.title")}</p>
              <h3 className="font-display text-2xl text-white mt-1">{count} {count !== 1 ? t("cart.items_plural") : t("cart.items")}</h3>
            </div>
            <button
              data-testid="cart-close-button"
              onClick={closeCart}
              className="text-white/70 hover:text-[#C5A059] p-2"
              aria-label={t("cart.title")}
            >
              <X size={22} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {items.length === 0 ? (
              <div data-testid="cart-empty-state" className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 border border-[#C5A059]/30 rounded-full flex items-center justify-center mb-6">
                  <ArrowRight size={20} className="text-[#C5A059]" />
                </div>
                <p className="font-display text-2xl text-white mb-2">{t("cart.empty")}</p>
                <p className="text-[#A1A1AA] text-sm mb-8 max-w-xs">
                  {t("cart.emptyDesc")}
                </p>
                <Link to="/sneakers" onClick={closeCart} data-testid="cart-explore-button" className="btn-gold">
                  {t("cart.explore")}
                </Link>
              </div>
            ) : (
              <ul className="space-y-6">
                {items.map((item) => (
                  <li
                    key={item.key}
                    data-testid={`cart-item-${item.slug}`}
                    className="flex gap-4 pb-6 border-b border-white/5"
                  >
                    <div className="w-24 h-24 bg-[#121212] shrink-0 overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="overline text-[10px] mb-1">{item.brand}</p>
                      <Link
                        to={`/sneakers/${item.slug}`}
                        onClick={closeCart}
                        className="block font-display text-base text-white leading-tight mb-1 hover:text-[#C5A059] transition"
                      >
                        {item.name}
                      </Link>
                      {item.size && (
                        <p className="text-xs text-white/50 mb-3">{t("cart.size")} {item.size}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <div className="inline-flex items-center border border-white/10">
                          <button
                            data-testid={`cart-decrement-${item.slug}`}
                            onClick={() => updateQty(item.key, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-[#C5A059]"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="w-8 text-center text-sm text-white">{item.quantity}</span>
                          <button
                            data-testid={`cart-increment-${item.slug}`}
                            onClick={() => updateQty(item.key, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-[#C5A059]"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <span className="font-grotesk text-sm text-[#C5A059]">
                          {(item.price * item.quantity).toLocaleString("fr-FR")} $
                        </span>
                      </div>
                    </div>
                    <button
                      data-testid={`cart-remove-${item.slug}`}
                      onClick={() => removeItem(item.key)}
                      className="text-white/30 hover:text-red-400 self-start"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t border-white/5 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="overline">{t("cart.subtotal")}</span>
                <span data-testid="cart-subtotal" className="font-display text-2xl text-white">
                  {subtotal.toLocaleString("fr-FR")} $
                </span>
              </div>
              <p className="text-[11px] text-white/40 leading-relaxed">
                {t("cart.disclaimer")}
              </p>
              <a
                href={`https://wa.me/243981962762?text=${encodeURIComponent(
                  t("cart.whatsappPrefix") + "\n\n" +
                    items
                      .map(
                        (i) => `• ${i.name}${i.size ? ` (T.${i.size})` : ""} x${i.quantity} — ${i.price * i.quantity}$`
                      )
                      .join("\n") +
                    `\n\nTotal: ${subtotal}$`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cart-checkout-whatsapp"
                className="btn-gold w-full justify-center"
              >
                {t("cart.checkout")}
              </a>
              <button
                data-testid="cart-clear-button"
                onClick={clear}
                className="w-full text-xs text-white/40 hover:text-white/70 uppercase tracking-[0.2em] font-grotesk pt-2"
              >
                {t("cart.clear")}
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default CartDrawer;
